import { useState, useEffect, useMemo, useCallback } from "react";

// Method families whose payout needs a destination wallet address. Mirrors the
// backend Withdrawal::isWalletAddressRequiredForWithDrawal (SKRILL:WLT,
// CRYPTOPAY:, Counos:, WEBPAYMENT:) plus COINSPAID: — the CoinsPaid/AlphaPo
// crypto payout the product owner requires ("must work like the deposit via
// Coinspaid/Alphapo"). The backend CoinsPaid driver sends
// withdrawal_account_number (the address) to the library, so it must be
// collected even though the gate helper omits COINSPAID.
export const WALLET_ADDRESS_PREFIXES = ["COINSPAID:", "CRYPTOPAY:", "Counos:", "WEBPAYMENT:"];

// True when the selected method needs a destination wallet address. The address
// is sent to /withdraw as the top-level `accNumber` (backend reads
// $request->accNumber -> withdrawal_account_number), NOT inside extraFields.
export function walletAddressRequired(methodId) {
  if (typeof methodId !== "string" || !methodId) return false;
  if (methodId === "SKRILL:WLT") return true;
  return WALLET_ADDRESS_PREFIXES.some((p) => methodId.startsWith(p));
}

// True only for CoinsPaid crypto: some coins (XRP, XLM, ...) need a tag/memo.
// Sent to /withdraw as the top-level `tag` (backend: COINSPAID branch reads
// $request->tag and, when the wallet has a coinspaid_tag, requires it, storing
// it as withdrawal_account_iban).
export function cryptoTagApplicable(methodId) {
  return typeof methodId === "string" && methodId.startsWith("COINSPAID:");
}

// Per-method payout inputs. `rawMethod` is the engine /withdraw/methods row (it
// carries method_id and, for PagSmile-style methods, a `fields` array). Returns
// the collected values + a validity flag; state resets whenever the selected
// method changes so a crypto address can never leak onto a different method's
// request.
export function useWithdrawExtras(rawMethod) {
  const methodId = rawMethod && rawMethod.method_id;
  const fields = useMemo(
    () => (rawMethod && Array.isArray(rawMethod.fields) ? rawMethod.fields : []),
    [rawMethod]
  );
  const addressRequired = walletAddressRequired(methodId);
  const tagApplicable = cryptoTagApplicable(methodId);

  const [address, setAddress] = useState("");
  const [tag, setTag] = useState("");
  const [extraFields, setExtraFields] = useState({});

  // Reset every input when the selected method changes.
  useEffect(() => {
    setAddress("");
    setTag("");
    setExtraFields({});
  }, [methodId]);

  const setExtraField = useCallback((name, value) => {
    setExtraFields((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Address (when required) and every declared PagSmile-style field must be
  // filled before the CTA enables. Tag stays optional client-side (the backend
  // enforces it per-coin) so tag-less coins are not blocked.
  const extrasValid = useMemo(() => {
    if (addressRequired && !String(address).trim()) return false;
    for (let i = 0; i < fields.length; i += 1) {
      const v = extraFields[fields[i].name];
      if (!v || !String(v).trim()) return false;
    }
    return true;
  }, [addressRequired, address, fields, extraFields]);

  const hasExtras = addressRequired || tagApplicable || fields.length > 0;

  return {
    fields,
    addressRequired,
    tagApplicable,
    hasExtras,
    address,
    setAddress,
    tag,
    setTag,
    extraFields,
    setExtraField,
    extrasValid,
  };
}
